import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Buildings, Warning, CheckCircle, MagnifyingGlass, Student, Bed, GraduationCap,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-1.5 text-xs">
    <Icon size={12} className="text-[var(--vs-text-secondary)]" />
    <span className="font-mono">{value}</span>
    <span className="text-[var(--vs-text-secondary)]">{label}</span>
  </div>
);

export default function TenantAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | drift | empty | active

  useEffect(() => {
    api.get("/admin/tenant-audit")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const ql = q.trim().toLowerCase();
    return data.trusts.filter((t) => {
      if (ql && !t.trust_name.toLowerCase().includes(ql) && !(t.trust_slug || "").toLowerCase().includes(ql)) return false;
      if (filter === "drift") return t.drift_flags.length > 0;
      if (filter === "empty") return t.counts.users === 0 && t.counts.students === 0 && !t.is_platform;
      if (filter === "active") return t.counts.users > 0 || t.counts.students > 0;
      return true;
    });
  }, [data, q, filter]);

  return (
    <DashboardLayout title="Tenant Audit" subtitle="Read-only one-pager. Verify isolation, spot drift, drill into per-trust data.">
      {loading && <div className="vs-card p-6 text-center text-[var(--vs-text-secondary)]">Loading…</div>}

      {data && (
        <>
          {/* Summary KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            {[
              { label: "Trusts", value: data.summary.total_trusts, color: "text-[var(--vs-text)]" },
              { label: "Users", value: data.summary.total_users, color: "text-[var(--vs-text)]" },
              { label: "Students", value: data.summary.total_students, color: "text-[var(--vs-text)]" },
              { label: "Hostels", value: data.summary.total_hostels, color: "text-[var(--vs-text)]" },
              { label: "Schools", value: data.summary.total_schools, color: "text-[var(--vs-text)]" },
              { label: "Drift", value: data.summary.trusts_with_drift, color: data.summary.trusts_with_drift > 0 ? "text-red-700" : "text-[var(--vs-success)]" },
            ].map((s) => (
              <div key={s.label} className="vs-card p-3" data-testid={`audit-summary-${s.label.toLowerCase()}`}>
                <div className={`text-2xl font-display font-semibold ${s.color}`}>{s.value}</div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--vs-text-secondary)] mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Drift banner (always visible) */}
          {data.summary.trusts_with_drift === 0 ? (
            <div className="vs-card p-4 mb-4 flex items-start gap-3 border-l-4 border-[var(--vs-success)]" data-testid="audit-drift-clean">
              <CheckCircle size={18} className="text-[var(--vs-success)] mt-0.5" weight="bold" />
              <div className="text-sm">
                <div className="font-semibold">No isolation drift detected.</div>
                <div className="text-xs text-[var(--vs-text-secondary)]">Every non-platform trust has only its own data. No stray super_admins or orphaned audit rows.</div>
              </div>
            </div>
          ) : (
            <div className="vs-card p-4 mb-4 flex items-start gap-3 border-l-4 border-red-500" data-testid="audit-drift-detected">
              <Warning size={18} className="text-red-600 mt-0.5" weight="bold" />
              <div className="text-sm">
                <div className="font-semibold text-red-700">
                  {data.summary.trusts_with_drift} trust{data.summary.trusts_with_drift > 1 ? "s have" : " has"} isolation drift.
                </div>
                <div className="text-xs text-[var(--vs-text-secondary)]">Investigate each row marked &quot;drift&quot; below.</div>
              </div>
            </div>
          )}

          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vs-text-secondary)]" />
              <Input
                placeholder="Search by trust name or slug…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8"
                data-testid="audit-search"
              />
            </div>
            {[
              { k: "all", label: "All" },
              { k: "drift", label: "Drift only" },
              { k: "empty", label: "Empty" },
              { k: "active", label: "Active" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-widest transition-colors ${
                  filter === f.k
                    ? "bg-[var(--vs-secondary)] text-[var(--vs-bg)]"
                    : "bg-[var(--vs-bg-subtle)] text-[var(--vs-text-secondary)] hover:bg-[var(--vs-text)]/10"
                }`}
                data-testid={`audit-filter-${f.k}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Audit table */}
          <div className="vs-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--vs-bg-subtle)]">
                <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
                  <th className="px-4 py-3">Trust</th>
                  <th className="px-4 py-3">Admins</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Subscription</th>
                  <th className="px-4 py-3">Last activity</th>
                  <th className="px-4 py-3">Drift</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No trusts match your filter.</td></tr>
                )}
                {filtered.map((t) => {
                  const subLabel = t.sub.status
                    ? `${t.sub.status}${t.sub.days_until_expiry !== null && t.sub.days_until_expiry !== undefined ? ` · ${t.sub.days_until_expiry}d` : ""}`
                    : "—";
                  const subColor =
                    t.sub.status === "disabled" ? "text-red-700"
                    : (t.sub.days_until_expiry !== null && t.sub.days_until_expiry !== undefined && t.sub.days_until_expiry < 0) ? "text-red-700"
                    : (t.sub.days_until_expiry !== null && t.sub.days_until_expiry !== undefined && t.sub.days_until_expiry <= 30) ? "text-amber-700"
                    : "text-[var(--vs-success)]";
                  return (
                    <tr key={t.trust_id} className="border-t border-[var(--vs-border)] align-top" data-testid={`audit-row-${t.trust_slug || t.trust_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 font-medium">
                          <Buildings size={14} className="text-[var(--vs-text-secondary)]" />
                          {t.trust_name}
                          {t.is_platform && <Badge variant="outline" className="border-[var(--vs-secondary)]/40 text-[var(--vs-secondary)] text-[10px]">platform</Badge>}
                        </div>
                        <div className="text-[10px] font-mono text-[var(--vs-text-secondary)] mt-1">{t.trust_slug}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="font-mono">{t.counts.users}</div>
                        <div className="text-[10px] text-[var(--vs-text-secondary)] mt-1 space-y-0.5">
                          {Object.entries(t.role_counts).slice(0, 5).map(([role, n]) => (
                            <div key={role}>{role}: {n}</div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <StatPill icon={Student} label="students" value={t.counts.students} />
                          <StatPill icon={Bed} label="hostels" value={t.counts.hostels} />
                          <StatPill icon={GraduationCap} label="schools" value={t.counts.schools} />
                          <div className="text-[10px] text-[var(--vs-text-secondary)] mt-1">
                            {t.counts.fees} fees · {t.counts.donations} donations · {t.counts.certificates} certs
                          </div>
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-xs ${subColor}`}>
                        <div className="font-medium">{subLabel}</div>
                        <div className="text-[10px] text-[var(--vs-text-secondary)] mt-1">{t.sub.paid_until || "no expiry"}</div>
                      </td>
                      <td className="px-4 py-3 text-[10px] text-[var(--vs-text-secondary)] font-mono">
                        {t.last_activity_at ? t.last_activity_at.slice(0, 19).replace("T", " ") : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {t.drift_flags.length === 0 ? (
                          <Badge variant="outline" className="border-[var(--vs-success)]/40 text-[var(--vs-success)] text-[10px]" data-testid={`audit-clean-${t.trust_slug || t.trust_id}`}>
                            clean
                          </Badge>
                        ) : (
                          <div className="space-y-1" data-testid={`audit-drift-${t.trust_slug || t.trust_id}`}>
                            {t.drift_flags.map((f, i) => (
                              <Badge key={i} variant="outline" className="border-red-500/40 text-red-700 text-[10px] block w-fit">
                                {f}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
